import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import './index.css';
import initI18n from './i18n';
import { translationsClient } from './lib/translations';
import { initAnalytics } from './lib/analytics/initAnalytics';
import { monitorNetworkStatus } from './utils/debugUtils';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 30,
      retry: 3,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    }
  }
});

window.__APP_MOUNTED = false;

async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers not supported');
    return;
  }
  try {
    const baseUrl = window.HOTEL_CONFIG?.baseUrl || '';
    const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
    const swPath = normalizedBaseUrl
      ? `${normalizedBaseUrl}/sw.js`
      : '/sw.js';
    const swScope = normalizedBaseUrl || '/';

    console.log('[SW] Registering service worker...');
    console.log('[SW] Path:', swPath);
    console.log('[SW] Scope:', swScope);

    const registration = await navigator.serviceWorker.register(swPath, { scope: swScope });
    console.log('[SW] Service worker registered successfully:', registration);
    console.log('[SW] Final scope:', registration.scope);
    console.log('[SW] Active:', registration.active);
    console.log('[SW] Installing:', registration.installing);
    console.log('[SW] Waiting:', registration.waiting);
  } catch (err) {
    console.warn('[SW] Service worker registration failed:', err);
  }
}

const init = async () => {
  try {
    if (window.__APP_MOUNTED) {
      return;
    }

    registerServiceWorker();

    await initAnalytics();
    monitorNetworkStatus();

    await Promise.all([
      translationsClient.getGeneralTranslations(),
      translationsClient.getCategoryTypeTranslations()
    ]);

    await initI18n();

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Root element not found');
    }

    const root = createRoot(rootElement);

    const baseUrl = window.HOTEL_CONFIG?.baseUrl || '';

    root.render(
      <React.StrictMode>
        <BrowserRouter basename={baseUrl}>
          <QueryClientProvider client={queryClient}>
            <App />
            {import.meta.env.DEV && <ReactQueryDevtools />}
          </QueryClientProvider>
        </BrowserRouter>
      </React.StrictMode>
    );

    window.__APP_MOUNTED = true;

  } catch (error) {
    console.error('Failed to initialize application:', error);
  }
};

init().catch(error => {
  console.error('Unhandled error in init function:', error);
});
